import {Box} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import React, {forwardRef, useImperativeHandle, useRef, useState} from 'react'
import lazyLoadable from '../../../../utils/lazy-loadable'
import Image from '../image'
import useOcapiSite from '../../../../hooks/useOcapiSite'
import useVideoAutoplay from '../../../hooks/useVideoAutoplay'
import {hasValidImageSrc, hasValidString} from '../../../utils/utils'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

const ReactPlayerLoader = lazyLoadable(() => import('@brightcove/react-player-loader'))

const muteForAutoplay = (player) => {
    if (player.muted()) return
    player.muted(true)
    player.volume(0)
}

/**
 * BrightcoveVideo component for rendering Brightcove video player
 * Parent component controls play/pause via ref
 * @component
 * @param {Object} props - Component properties
 * @param {string} props.brightcoveId - Brightcove video ID
 * @param {Object} [props.poster] - Poster image object (see Image component) - shown until video starts playing
 * @param {string} [props.poster.src] - Image source URL (automatically converted to DIS URL if applicable)
 * @param {string} [props.poster.disBaseLink] - Optional DIS base link for responsive images (auto-generated from src if not provided)
 * @param {string} [props.poster.alt] - Alt text for accessibility
 * @param {string} [props.poster.title] - Image title
 * @param {boolean} [props.autoplay=false] - Whether to autoplay the video (will be muted)
 * @param {boolean} [props.videoControls=false] - Whether to show Brightcove player controls
 * @param {('eager'|'lazy')} [props.loading='lazy'] - Loading strategy for poster image
 * @param {string} [props.title] - Video title
 */
const BrightcoveVideo = forwardRef(
    (
        {
            brightcoveId,
            poster,
            autoplay = false,
            videoControls = false,
            loading = 'lazy',
            title,
            objectFit = 'contain',
            widths,
            videoType,
            onPlay
        },
        ref
    ) => {
        const isFullVideoMode = videoType === 'video'
        const playerRef = useRef(null)
        const [playerReady, setPlayerReady] = useState(false)
        const [hasStartedPlaying, setHasStartedPlaying] = useState(false)
        const {accountId, assemblyVideoPlayerId} = useOcapiSite()
        const shouldAutoplay = useVideoAutoplay(isFullVideoMode ? false : autoplay)
        // keep track of the last requested player state so it can be applied when the player loads
        const playerRequestedStateRef = useRef(shouldAutoplay)
        // mute requested before the player loaded
        const playerRequestedMuteRef = useRef(false)

        // In full video mode: force native controls on, don't mute
        const effectiveControls = isFullVideoMode ? true : videoControls
        const effectiveMuted = isFullVideoMode ? false : shouldAutoplay

        // Expose play/pause methods to parent
        useImperativeHandle(ref, () => ({
            play: (muteVideo) => {
                const player = playerRef.current?.player_
                if (player) {
                    // In case we want to imperatively play the video without any user interaction
                    // then the video must be muted due to autoplay policy
                    //
                    // https://developer.chrome.com/blog/autoplay/
                    if (muteVideo) muteForAutoplay(player)

                    player.play()
                } else if (muteVideo) {
                    playerRequestedMuteRef.current = true
                }
                playerRequestedStateRef.current = true
            },
            pause: () => {
                playerRef.current?.player_?.pause()
                playerRequestedStateRef.current = false
            }
        }))

        if (!hasValidString(brightcoveId)) {
            console.warn('BrightcoveVideo: brightcoveId is required')
            return null
        }

        if (!accountId) {
            console.warn('BrightcoveVideo: accountId not available from site preferences')
            return null
        }

        if (!assemblyVideoPlayerId) {
            console.warn(
                'BrightcoveVideo: assemblyVideoPlayerId not available from site preferences'
            )
            return null
        }

        const onSuccess = ({ref: videoRef}) => {
            playerRef.current = videoRef
            const {player_} = videoRef

            // Set player styles
            player_.el().style.width = '100%'
            player_.el().style.height = '100%'

            // #202: Unnecessary ARIA attributes can cause unexpected behavior for screen reader users.
            // remove aria-live from the play button label
            player_.bigPlayButton?.controlTextEl_?.removeAttribute('aria-live')

            // Always hide the native big play button — we provide our own VideoControl
            // (bottom-right for animation mode, centered overlay for full video mode)
            player_.bigPlayButton?.hide()

            // Force controls visibility based on effective controls
            if (effectiveControls) {
                player_.controls(true)
            } else {
                player_.controls(false)
            }

            // Disable loop in full video mode — loop is animation-only behaviour
            if (isFullVideoMode) {
                player_.loop(false)
            }

            // Track if video has started playing to hide custom poster
            player_.on('play', () => {
                setHasStartedPlaying(true)
                onPlay?.()
            })

            // play/pause in case we got that action
            // before the video player was loaded
            // and it differs from the autoplay state
            if (playerRequestedStateRef.current) {
                if (playerRequestedMuteRef.current) muteForAutoplay(player_)
                player_.play()
            } else if (!playerRequestedStateRef.current) {
                player_.pause()
            }

            setPlayerReady(true)
        }

        return (
            <Box
                position="relative"
                width="100%"
                height="100%"
                data-testid="brightcove-video"
                data-player-ready={playerReady ? 'true' : 'false'}
                css={{
                    '& .brightcove-react-player-loader>*': {
                        width: '100%',
                        height: '100%'
                    },
                    '& .vjs-tech': {objectFit},
                    // if data-player-ready is true hide brightcove poster
                    '&[data-player-ready="true"] .vjs-poster': {
                        opacity: 0,
                        pointerEvents: 'none'
                    }
                }}
            >
                <ReactPlayerLoader
                    attrs={{
                        style: {
                            width: '100%',
                            height: '100%'
                        }
                    }}
                    props={{
                        style: {
                            height: '100%'
                        }
                    }}
                    accountId={accountId}
                    playerId={assemblyVideoPlayerId}
                    videoId={brightcoveId}
                    options={{
                        autoplay: shouldAutoplay,
                        muted: effectiveMuted,
                        // without it iOS Safari plays only in its fullscreen native player,
                        // which full video keeps
                        playsinline: !isFullVideoMode
                    }}
                    // disable brightcove poster when we can render a responsive poster
                    // the brightcove poster is not responsive and is downloading the maximum image size
                    {...(hasValidImageSrc(poster) && {
                        poster: getAssetUrl('static/img/transparent.png')
                    })}
                    embedOptions={{
                        responsive: true
                    }}
                    onSuccess={onSuccess}
                    aria-label={title}
                />

                {/* SSR poster image - shown until video starts playing */}
                {hasValidImageSrc(poster) && !hasStartedPlaying && (
                    <Box
                        position="absolute"
                        top="0"
                        left="0"
                        width="100%"
                        height="100%"
                        aria-hidden="true"
                    >
                        <Image
                            {...poster}
                            widths={widths}
                            objectFit={objectFit}
                            width="100%"
                            height="100%"
                            borderRadius="0"
                            loading={loading}
                            preload={loading === 'eager'}
                        />
                    </Box>
                )}
            </Box>
        )
    }
)

if (process.env.NODE_ENV === 'development') {
    BrightcoveVideo.displayName = 'BrightcoveVideo'
}

BrightcoveVideo.propTypes = {
    brightcoveId: PropTypes.string,
    poster: PropTypes.object,
    autoplay: PropTypes.bool,
    videoControls: PropTypes.bool,
    loading: PropTypes.oneOf(['eager', 'lazy']),
    title: PropTypes.string,
    objectFit: PropTypes.string,
    widths: PropTypes.object,
    videoType: PropTypes.oneOf(['animation', 'video']),
    onPlay: PropTypes.func
}

export default BrightcoveVideo
